// Ported from src/components/DeviceControls/DeviceControls.js on 2026-10-05.
import LegacyTextToggle from 'components/UI/TextToggle';
import type { DeviceControlToggleComponent } from 'features/Integrations/types/deviceControl.types';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { Button, Form } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import type {
  DeviceCommandFieldsProps,
  DeviceControlField,
} from 'features/Integrations/types/deviceControl.types';

const TextToggle = LegacyTextToggle as DeviceControlToggleComponent;

export default function DeviceCommandFields({
  component,
  device,
  setData,
  compact,
  removeMe,
  setRemoveMe,
  changes,
  sending,
  audioRequired,
  audioOverride,
  on,
  displayMessage,
  isOverrideAvailable,
  isOverrideActive,
  isTargetLocked,
}: DeviceCommandFieldsProps) {
  const renderLabel = (component: DeviceControlField) => {
    const label = component.label.replace(/{(.*?)}/g, (_, key) => {
      return String(device[key as keyof typeof device] ?? '');
    });
    return (
      <div>
        <label>{label}</label>
        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  const renderTextToggle = (component: DeviceControlField) => {
    let properties = {
      disabled: isTargetLocked && !isOverrideActive,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row justify-content-between">
          <div className="flex-fill flex-bases-0">
            <label>{component.label}</label>
          </div>
          <div className="flex-fill flex-bases-0">
            <TextToggle {...properties} />
          </div>
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  const renderTextArea = (component: DeviceControlField) => {
    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row'} align-items-baseline gap-2`}
        >
          <label>{component?.label}</label>
          <Form.Control
            as="textarea"
            className="border border-white rounded"
            placeholder={component?.props?.placeholder}
            onChange={(e) =>
              setData({
                ...device,
                [component?.props?.onChange]: e.target.value,
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  const renderIntervalInput = (component: DeviceControlField) => {
    if (!device?.status) return null;

    const {
      text,
      disableValue,
      maxValue,
      minValue,
      value,
      repeatValue,
      repeatForeverLabel,
      repeatForeverValue,
    } = component.props;

    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row align-items-lg-center'} align-items-stretch gap-2`}
        >
          <Button
            variant="link"
            className="position-relative p-0 text-white text-decoration-none"
            onClick={() => setRemoveMe(!removeMe)}
          >
            <div className="d-flex gap-1 align-items-center">
              <FaInfoCircle /> <label>{component?.label}</label>
            </div>

            <div
              className={`${
                removeMe ? 'd-block' : 'd-none'
              } position-absolute bg-white text-black rounded-2 p-3 z-2 shadow`}
              style={{
                top: 'calc(100% + 0.5rem)',
                left: 0,
                width: 'clamp(280px, 34vw, 440px)',
                maxWidth: 'min(440px, calc(100vw - 4rem))',
                maxHeight: 'min(320px, calc(100vh - 12rem))',
                overflowY: 'auto',
                overflowX: 'hidden',
                whiteSpace: 'normal',
              }}
            >
              <p className="m-0 text-start">
                This setting controls how often the event should automatically repeat, measured in
                seconds. It's especially useful for scenarios where an action, such as a siren
                activation or audible alert, needs to occur on a recurring loop. For example, if
                you're triggering a 30-second siren tone, you can set it to repeat continuously
                every 30 seconds, every 15 minutes, or even once every few hours depending on your
                operational needs. Use this field to customize the spacing between each activation
                cycle. The system will re-trigger the event at the defined interval as long as the
                condition remains true and the Global Action is active. Valid input range: 30 to
                7200 seconds (i.e., from every 30 seconds to every 2 hours).
              </p>
            </div>
          </Button>

          <div
            className={compact ? 'w-100' : 'w-25'}
            style={compact ? { maxWidth: 180 } : undefined}
          >
            <TextToggle
              options={['Off', 'On']}
              value={device[value] !== undefined && device[value] !== disableValue}
              setValue={(_value) => {
                if (_value)
                  setData({
                    ...device,
                    [value]: minValue || 0,
                  });
                else
                  setData({
                    ...device,
                    [value]: disableValue || 0,
                  });
              }}
              invert
            />
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              aria-label={component?.label || 'Repeat interval'}
              disabled={device[value] === disableValue}
              type="number"
              value={(device[value] as string | number) || 0}
              onChange={(e) =>
                setData({
                  ...device,
                  [value]: e.target.value,
                })
              }
              onBlur={() =>
                setData({
                  ...device,
                  [value]: Math.max(minValue, Math.min(maxValue, Number(device[value]))),
                })
              }
            />
            {text && <p className="m-0">{text}</p>}
          </div>
        </div>

        {component.countEnabled && (
          <div
            className={`d-flex ${
              compact
                ? 'flex-wrap align-items-end'
                : 'flex-column flex-lg-row align-items-stretch align-items-lg-center'
            } gap-2 mt-2`}
          >
            <div>
              <label>{component.countLabel}</label>
            </div>

            <div>
              {device[repeatValue] !== repeatForeverValue && (
                <Form.Control
                  aria-label={component.countLabel || 'Repeat count'}
                  disabled={device[value] === disableValue}
                  type="number"
                  value={device[repeatValue] || 0}
                  onChange={(e) =>
                    setData({
                      ...device,
                      [repeatValue]: e.target.value,
                    })
                  }
                  onBlur={() =>
                    setData({
                      ...device,
                      [repeatValue]: Math.max(Number(device[repeatValue]), 1),
                    })
                  }
                />
              )}
            </div>

            <div className="d-flex align-items-center gap-2 pb-1">
              <Form.Check
                type="checkbox"
                disabled={device[value] === disableValue}
                checked={device[repeatValue] === repeatForeverValue}
                onChange={(e) =>
                  setData({
                    ...device,
                    [repeatValue]: e.target.checked ? repeatForeverValue : 0,
                  })
                }
              />
              <p className="m-0">{repeatForeverLabel}</p>
            </div>
          </div>
        )}

        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  const renderSlider = (component: DeviceControlField) => {
    if (!device?.status) return null;

    const { value, min, max } = component.props;
    const sliderValue = device?.[value] ?? max;

    return (
      <div>
        <div
          className={`d-flex flex-column ${compact ? '' : 'flex-lg-row align-items-lg-center'} gap-4`}
        >
          <label className="mb-0 flex-shrink-0" style={{ minWidth: 96 }}>
            {component.label} {sliderValue}%
          </label>
          <Form.Range
            className="w-100"
            style={{ maxWidth: 240 }}
            value={sliderValue as string | number}
            min={min}
            max={max}
            onChange={(e) =>
              setData({
                ...device,
                [value]: Math.min(Math.max(Number(e.target.value), min), max),
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };
  const renderExternalShare = () => {
    //TODO: If this gets re-enabled it will need to be bootstrapped.
    return null;
  };
  const renderUnsavedChanges = () => {
    return (
      <div className={`${changes ? 'd-block' : 'd-none'}`}>
        *You have unsaved changes, Discard or Save to continue*
      </div>
    );
  };
  const renderSaveButton = () => {
    return (
      <div className={`d-flex flex-fill justify-content-end ${compact ? 'p-2' : ''}`}>
        {sending && (
          <div>
            <WidgetLoadingAnimation disableText widgetName={undefined} dimensions={undefined} />
          </div>
        )}
        <Button
          className={compact ? 'px-3 py-2' : 'p-3'}
          disabled={
            // A maintenance operator's active override lifts the maintenance gate, as on the toggle.
            (isTargetLocked && !isOverrideActive) ||
            (audioRequired && device?.status && device?.audioIndex < 0 && !audioOverride) ||
            sending
          }
          onClick={() => on('submit', { ...device, maintenanceOverride: isOverrideActive })}
        >
          Save
        </Button>
      </div>
    );
  };
  const renderMaintenanceOverride = () => {
    if (isOverrideAvailable)
      return (
        <div className="d-flex gap-2 align-items-baseline">
          <Form.Check
            type="checkbox"
            checked={!!device?.maintenanceOverride}
            onChange={(e) => {
              setData({ ...device, maintenanceOverride: e.target.checked });
            }}
          />
          <p>
            Override <span className="text-warning">Maintenance</span>?
          </p>
        </div>
      );
  };
  const renderDisplayMessage = () => {
    if (displayMessage)
      return (
        <p className={displayMessage?.type === 'error' ? 'text-error' : 'text-success'}>
          {displayMessage.message}
        </p>
      );
  };
  switch (component.type) {
    case 'Label':
      return renderLabel(component);
    case 'TextToggle':
      return renderTextToggle(component);
    case 'textarea':
      return renderTextArea(component);
    case 'interval-input':
      return renderIntervalInput(component);
    case 'slider':
      return renderSlider(component);
    case 'send-status-to':
      return renderExternalShare();
    case 'unsaved-changes-label':
      return renderUnsavedChanges();
    case 'save-button':
      return renderSaveButton();
    case 'maintenance-override':
      return renderMaintenanceOverride();
    case 'display-message':
      return renderDisplayMessage();
    default:
      return null;
  }
}
